/**
 * @file src/local/localByteVault.js
 * @description The copy of a local file that this browser keeps, and everything that copy owes.
 *
 * A file the user dropped is copied once into the **origin private file system** — the browser's
 * own private folder for this origin, reachable by no other site, never synced, never uploaded,
 * and invisible in the user's file manager. From then on the library plays its own copy, which is
 * why a reload keeps working in Firefox, Safari, Brave and on a phone, where nothing can be
 * remembered about a file the app does not hold.
 *
 * The desktop shell copies nothing: it has a real path and reads the actual file (see
 * `localFileAccess.js`), so a copy there would cost disk and buy nothing.
 *
 * ## What a copy owes
 *
 * Bytes on someone's disk are a debt, not an asset. The rules, all enforced here:
 *
 *   - one copy per entry, written on import, named by the entry's id and nothing else;
 *   - **the cover beside it**, under the same id with its own suffix, so the picture a file
 *     carried lives and dies with the entry rather than in a second store with a second
 *     lifecycle to keep honest;
 *   - **deleted when the entry is removed** — `removeLocalEntry` cannot succeed without it;
 *   - **swept when it is orphaned** — a copy whose entry is gone (a tab closed mid-import, a
 *     store repaired by delete) is found on boot and deleted, so a library that says it holds
 *     nothing really holds nothing;
 *   - countable, so the rail can say how much of this machine it is using;
 *   - and **allowed to vanish**. Safari evicts unpersisted storage. A copy that is gone reads as
 *     a missing file — the same state as a file moved on disk — not as an error.
 *
 * ## What is never here
 *
 * No hosted audio (that is Cache Storage, `offlineKeys.js`, a different lifecycle and a different
 * key scheme). No filenames leaving the machine. This is the user's own audio, on the user's own
 * disk, and it goes nowhere.
 */

/** The OPFS subdirectory. Everything the library owns lives under it and nothing else does. */
const VAULT_DIR = 'local-audio';

/** Copy in slices so a long file reports progress and never lands in memory whole twice. */
const COPY_CHUNK_BYTES = 8 * 1024 * 1024;

/**
 * Refuse an import that would leave less than this much of the browser's quota free. A library
 * that fills the quota takes the offline collections down with it — they share one budget.
 */
const QUOTA_HEADROOM_BYTES = 256 * 1024 * 1024;

/**
 * The name of the cross-tab lock. Copies and the orphan sweep both take it, so a sweep can never run
 * while another tab is part-way through creating the copy it would call an orphan, and two tabs
 * cannot both pass the quota check on the same free space.
 */
const VAULT_LOCK = 'plantasia-local-audio-vault';

/**
 * Run `work` with the vault to itself. Falls back to running unguarded where Web Locks do not
 * exist — one tab is still correct, and refusing to work at all would be worse.
 */
export async function withVaultLock(work) {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
  if (!locks || typeof locks.request !== 'function') return work();
  return locks.request(VAULT_LOCK, work);
}

function storageManager() {
  return typeof navigator !== 'undefined' && navigator.storage ? navigator.storage : null;
}

/**
 * Whether this browser can hold a copy. Needs the directory AND `createWritable` — Safari shipped
 * the private file system a version before it could write to one from the main thread, and a
 * browser with only half of it must fall back rather than fail at the first import.
 */
export function vaultSupported() {
  const storage = storageManager();
  if (!storage || typeof storage.getDirectory !== 'function') return false;
  return (
    typeof FileSystemFileHandle !== 'undefined' &&
    typeof FileSystemFileHandle.prototype?.createWritable === 'function'
  );
}

let vaultDirPromise = null;

async function vaultDirectory({ create = true } = {}) {
  if (!vaultSupported()) return null;
  if (!vaultDirPromise) {
    vaultDirPromise = (async () => {
      const root = await storageManager().getDirectory();
      return root.getDirectoryHandle(VAULT_DIR, { create: true });
    })().catch((error) => {
      console.warn('[local] the private file system did not open', error);
      vaultDirPromise = null;
      return null;
    });
  }
  const directory = await vaultDirPromise;
  if (!directory && create) return null;
  return directory;
}

/** What an entry may keep here: its audio, and the cover the file carried. */
const AUDIO = 'audio';
const COVER = 'cover';

/**
 * The file name for one of an entry's belongings. The id is `local:<uuid>`, and only the uuid half
 * is used: a colon is legal in OPFS but not everywhere it might later be mirrored, and the prefix
 * says nothing a directory of exactly one kind of thing needs said.
 *
 * The COVER lives here too, under the same uuid with its own suffix, rather than in a store of its
 * own. One directory means one lock, one sweep, one measurement and one delete — an image store
 * beside this one would be a second lifecycle to keep honest, and this is the surface whose whole
 * job is being right about the disk.
 */
function fileNameFor(id, kind = AUDIO) {
  if (typeof id !== 'string') return null;
  const slug = id.startsWith('local:') ? id.slice('local:'.length) : id;
  // A name is derived, never passed through: an id that is not the shape we mint gets no file.
  return /^[a-f0-9-]{36}$/i.test(slug) ? `${slug}.${kind}` : null;
}

/** The entry id a file in the vault belongs to, or null if it is not one of ours. */
function idOfFileName(name) {
  const match = /^([a-f0-9-]{36})\.(audio|cover)$/i.exec(name);
  return match ? { id: `local:${match[1]}`, kind: match[2] } : null;
}

/** True when this browser already holds a copy for `id`. */
export async function hasVaultCopy(id) {
  const name = fileNameFor(id);
  const directory = name ? await vaultDirectory() : null;
  if (!directory) return false;
  try {
    await directory.getFileHandle(name);
    return true;
  } catch {
    return false;
  }
}

/**
 * The copy, as a `File`, or null when this browser does not have one — never copied, or evicted
 * since. The caller decides what a null means; to the library it means the same as a file that
 * moved on disk.
 */
export async function readVaultCopy(id) {
  const name = fileNameFor(id);
  const directory = name ? await vaultDirectory() : null;
  if (!directory) return null;
  try {
    const handle = await directory.getFileHandle(name);
    const file = await handle.getFile();
    // A zero-byte copy is an import that was interrupted, not a file. Treat it as absent so the
    // entry offers "add it again" instead of failing to decode.
    return file.size > 0 ? file : null;
  } catch {
    return null;
  }
}

/**
 * Copy a file into this browser. Returns the bytes written, or throws with a `code`:
 * `unsupported` (no private file system), `quota` (not enough room), `write` (anything else).
 *
 * @param {string} id the entry's `local:` id
 * @param {Blob} blob the file's bytes
 * @param {{ onProgress?: (fraction: number) => void, signal?: AbortSignal }} [options]
 */
export async function writeVaultCopy(id, blob, { onProgress, signal } = {}) {
  const name = fileNameFor(id);
  if (!name) throw Object.assign(new Error('Not a local entry id.'), { code: 'write' });
  const directory = await vaultDirectory();
  if (!directory) throw Object.assign(new Error('This browser keeps no copies.'), { code: 'unsupported' });
  // `readVaultCopy` reads a zero-byte file as absent, so writing one would mark the entry kept and
  // then report it missing for ever. Refuse it here, where it can still be said out loud.
  if (blob.size <= 0) throw Object.assign(new Error('That file is empty.'), { code: 'write' });
  await assertRoomFor(blob.size);

  // Whether a good copy already existed. A failed REPLACEMENT must not destroy it: the cleanup
  // below removes what this call created, never what it found.
  const replacing = await hasVaultCopy(id);

  let handle;
  let writable;
  try {
    handle = await directory.getFileHandle(name, { create: true });
    writable = await handle.createWritable();
  } catch (error) {
    throw Object.assign(new Error(error?.message || 'The copy could not be started.'), { code: 'write' });
  }

  try {
    let written = 0;
    while (written < blob.size) {
      if (signal?.aborted) throw Object.assign(new Error('Cancelled.'), { code: 'cancelled' });
      const slice = blob.slice(written, Math.min(written + COPY_CHUNK_BYTES, blob.size));
      await writable.write(await slice.arrayBuffer());
      written += slice.size;
      onProgress?.(blob.size > 0 ? written / blob.size : 1);
    }
    await writable.close();
    return written;
  } catch (error) {
    // A half-written copy is worse than none — it decodes to noise or not at all. Take it back,
    // unless a good one was already there, in which case aborting the writable has left it alone
    // and deleting now would lose a file for the sake of a failed refresh.
    try {
      await writable.abort?.();
    } catch {
      /* the abort of an already-broken writable tells us nothing */
    }
    if (!replacing) await deleteVaultCopy(id);
    if (error?.code) throw error;
    const quota = error?.name === 'QuotaExceededError';
    throw Object.assign(new Error(quota ? 'There is no room left in this browser.' : error?.message || 'The copy failed.'), {
      code: quota ? 'quota' : 'write',
    });
  }
}

/**
 * Delete this browser's copy. True when the bytes are gone — INCLUDING when there were none, since
 * that is the same end state. False only when a copy is still there and would not go, which the
 * caller has to be able to tell apart from success before it tells someone the file was deleted.
 */
export async function deleteVaultCopy(id) {
  const name = fileNameFor(id);
  const directory = name ? await vaultDirectory({ create: false }) : null;
  if (!directory) return !name ? false : true;
  // The cover goes with the audio, always. It belongs to the entry, not to the file it came from,
  // and a cover left behind is exactly the orphan the sweep exists to stop happening.
  await removeIfPresent(directory, fileNameFor(id, COVER));
  try {
    await directory.removeEntry(name);
    return true;
  } catch {
    // Already absent is a success; anything else is a copy we failed to remove.
    return !(await hasVaultCopy(id));
  }
}

async function removeIfPresent(directory, name) {
  if (!name) return;
  try {
    await directory.removeEntry(name);
  } catch {
    // Not there, or would not go. Neither decides whether the audio was removed.
  }
}

/** Every entry id this browser holds anything for — audio, a cover, or both. */
export async function listVaultIds() {
  const directory = await vaultDirectory({ create: false });
  if (!directory) return [];
  const ids = new Set();
  try {
    for await (const [name, handle] of directory.entries()) {
      if (handle.kind !== 'file') continue;
      const held = idOfFileName(name);
      if (held) ids.add(held.id);
    }
  } catch (error) {
    console.warn('[local] the copies could not be listed', error);
  }
  return [...ids];
}

/**
 * How much of this machine the vault is using, in bytes — the covers counted with the audio, since
 * both are bytes this app put on somebody's disk and the page that reports this says exactly that.
 */
export async function vaultUsageBytes() {
  const directory = await vaultDirectory({ create: false });
  if (!directory) return 0;
  let total = 0;
  try {
    for await (const [name, handle] of directory.entries()) {
      if (handle.kind !== 'file' || !idOfFileName(name)) continue;
      total += (await handle.getFile()).size;
    }
  } catch (error) {
    console.warn('[local] the copies could not be measured', error);
  }
  return total;
}

/**
 * The cover this browser kept for an entry, or null. Small, already downscaled by whoever wrote it
 * (`localCovers.js`), and read on demand rather than at boot: a library of five hundred files
 * should not open five hundred images to draw the dozen cards on screen.
 */
export async function readVaultCover(id) {
  const name = fileNameFor(id, COVER);
  const directory = name ? await vaultDirectory({ create: false }) : null;
  if (!directory) return null;
  try {
    const file = await (await directory.getFileHandle(name)).getFile();
    return file.size > 0 ? file : null;
  } catch {
    return null;
  }
}

/**
 * Keep an entry's cover. Never throws: a cover is a nicety, and a card with a letter on it instead
 * of a picture is not a failure worth taking an import down for.
 * @returns {Promise<boolean>} whether the bytes really landed
 */
export async function writeVaultCover(id, blob) {
  const name = fileNameFor(id, COVER);
  if (!name || !blob?.size) return false;
  try {
    const directory = await vaultDirectory();
    if (!directory) return false;
    // Covers are kilobytes, so this is one write rather than the audio's slice-by-slice loop —
    // and it still asks for room first, because it shares the quota the collections live in.
    await assertRoomFor(blob.size);
    const writable = await (await directory.getFileHandle(name, { create: true })).createWritable();
    await writable.write(await blob.arrayBuffer());
    await writable.close();
    return true;
  } catch (error) {
    console.warn('[local] this browser could not keep a cover', error);
    await deleteVaultCover(id);
    return false;
  }
}

/** Drop just the cover, leaving the audio. Used when a merge takes the survivor's picture. */
export async function deleteVaultCover(id) {
  const directory = await vaultDirectory({ create: false });
  if (directory) await removeIfPresent(directory, fileNameFor(id, COVER));
}

/**
 * Delete every copy with no entry behind it. Called on boot: a tab closed mid-import, or a store
 * that had to be repaired by deletion, leaves bytes nothing will ever ask for again, and a library
 * that reports an empty rail must not be quietly holding a gigabyte.
 *
 * @param {Iterable<string>} keepIds the ids the library actually has
 * @returns {Promise<{ deleted: number, bytes: number }>}
 */
export async function sweepVaultOrphans(keepIds) {
  const keep = new Set(keepIds);
  const held = await listVaultIds();
  let deleted = 0;
  let bytes = 0;
  for (const id of held) {
    if (keep.has(id)) continue;
    // Both belongings, since `deleteVaultCopy` takes both.
    const size = ((await readVaultCopy(id))?.size ?? 0) + ((await readVaultCover(id))?.size ?? 0);
    if (await deleteVaultCopy(id)) {
      deleted += 1;
      bytes += size;
    }
  }
  if (deleted) console.info(`[local] swept ${deleted} orphaned ${deleted === 1 ? 'copy' : 'copies'}`);
  return { deleted, bytes };
}

/**
 * Ask the browser to stop evicting this origin's storage. Safari in particular clears unpersisted
 * data, which would take the library's copies with it. Granted silently on engagement in most
 * browsers; a refusal is not an error, only a library that may have to be rebuilt one day.
 * @returns {Promise<boolean>} whether storage is persisted now
 */
export async function ensurePersistentStorage() {
  const storage = storageManager();
  if (!storage || typeof storage.persist !== 'function') return false;
  try {
    if (typeof storage.persisted === 'function' && (await storage.persisted())) return true;
    return await storage.persist();
  } catch {
    return false;
  }
}

/** `{ usage, quota }` for the whole origin, or nulls when the browser will not say. */
export async function readStorageEstimate() {
  const storage = storageManager();
  if (!storage || typeof storage.estimate !== 'function') return { usage: null, quota: null };
  try {
    const { usage = null, quota = null } = await storage.estimate();
    return { usage, quota };
  } catch {
    return { usage: null, quota: null };
  }
}

/** Throws with code `quota` when `bytes` would not fit with headroom to spare. */
async function assertRoomFor(bytes) {
  const { usage, quota } = await readStorageEstimate();
  if (usage === null || quota === null) return; // A browser that will not say is taken at its word.
  if (usage + bytes + QUOTA_HEADROOM_BYTES <= quota) return;
  throw Object.assign(new Error('There is no room left in this browser.'), { code: 'quota' });
}

export function resetLocalByteVaultForTests() {
  vaultDirPromise = null;
}
